import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { useStream } from '@laravel/stream-react';

import { CypressIds } from '../../../constants/cypressIds';
import { CertSubmissionDuskIds } from '../../../constants/duskIds';
import { createErrorToast, createToast } from '../../../redux/modules/app';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';
import BackgroundBlur from '../../BackgroundBlur';
import LoadingText from '../../LoadingText';

import OcrInfoBubble from './OcrInfoBubble';

const OcrProcessingStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const { id: recordId } = useSelector(blueprint.selectInsuranceRequirement);
  const documents = useSelector(blueprint.selectAllDocuments);

  const containerRef = useRef(null);

  const {
    data: policyDataStream,
    isFetching: policyDataStreamIsFetching,
    isStreaming: policyDataStreamIsStreaming,
    send,
    cancel,
    error
  } = useStream(
    `/api/v1/records/${recordId}/gpt-requests/policies`,
    {
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json'
      },
      withCredentials: true
    }
  );

  // ================================
  // Buffered Streaming State
  // ================================

  const [displayedStream, setDisplayedStream] = useState('');
  const streamBufferRef = useRef('');
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (!policyDataStream) return;

    // Update buffer only
    streamBufferRef.current = policyDataStream;

    if (flushTimerRef.current) return;

    flushTimerRef.current = setTimeout(() => {
      setDisplayedStream(streamBufferRef.current);
      flushTimerRef.current = null;
    }, 40); // 30–50ms ideal
  }, [policyDataStream]);

  // ================================
  // Smart Sticky Scroll
  // ================================

  const [isAtBottom, setIsAtBottom] = useState(true);

  const isUserNearBottom = (element, threshold = 80) => {
    if (!element) return false;

    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;

    return distance < threshold;
  };

  const handleScroll = () => {
    const element = containerRef.current;
    if (!element) return;
    setIsAtBottom(isUserNearBottom(element));
  };

  const scrollToBottom = () => {
    const element = containerRef.current;
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    if (!isAtBottom) return;

    element.scrollTop = element.scrollHeight;
  }, [displayedStream]);

  // ================================
  // Trigger AI Scan
  // ================================

  const triggerScan = () => {
    const documentIds = documents.flatMap(d => d.preparedDocuments.map(p => p.id));

    if (documentIds.length) {
      try {
        send({
          document_ids: documentIds,
          stream: true
        });
      } catch (e) {
        dispatch(createToast({
          title: 'Please manually enter your policy details.',
          status: 'warning',
          duration: 5000
        }));

        dispatch(blueprint.nextStep());
      }
    }
  };

  useEffect(() => {
    triggerScan();
  }, [documents]);

  // ================================
  // Connection timeout check
  // ================================

  const connectionTimeoutRef = useRef(null);
  const [showRetry, setShowRetry] = useState(false);

  const retryLogic = () => {
    triggerScan();
    setShowRetry(false);
  };

  useEffect(() => {
    // If we just started fetching, start timeout
    if (policyDataStreamIsFetching) {
      connectionTimeoutRef.current = setTimeout(() => {
        // If still fetching and NOT streaming → connection likely failed
        if (policyDataStreamIsFetching && !policyDataStreamIsStreaming) {
          cancel(); // stop the request

          dispatch(createToast({
            title: 'Connection timed out.',
            description: 'Please click the "Retry" button. If you continue to experience connection issues, click "Skip" to manually enter your policy data.',
            status: 'warning',
            duration: 7000
          }));

          setShowRetry(true);
        }
      }, 60000);
    }

    // If streaming starts or finishes, clear timeout
    if (policyDataStreamIsStreaming || policyDataStream) {
      clearTimeout(connectionTimeoutRef.current);
    }

    return () => {
      clearTimeout(connectionTimeoutRef.current);
    };
  }, [policyDataStreamIsFetching, policyDataStreamIsStreaming, policyDataStream]);

  // ================================
  // Begin Data Parse After Stream Completes
  // ================================
  useEffect(() => {
    // if policy data exists and we are done streaming/fetching, then parse the data
    if (policyDataStream && !policyDataStreamIsStreaming && !policyDataStreamIsFetching) {
      try {
        dispatch(blueprint.extractPolicyDataFromOpenAiResponse(policyDataStream));
      } catch (e) {
        dispatch(createToast({
          title: 'PINS was unable to extract your policy details',
          description: 'Please click Retry to try again or Skip to submit your policy details manually',
          status: 'warning',
          duration: 7000
        }));

        setShowRetry(true);
      }
    }
  }, [policyDataStream, policyDataStreamIsStreaming, policyDataStreamIsFetching]);

  const handleSkipOcr = (cancelOcr = false) => {
    if (cancelOcr) dispatch(blueprint.setOcrIsCanceled(true));
    dispatch(blueprint.nextStep());
  };

  return (
    <Flex backgroundColor="white" flexBasis="100%" flexFlow="row" overflowY="auto" width="100%">
      <BackgroundBlur left="30%" size="563px" top="10px" />

      <Flex direction="column" flexBasis="70%" height="100%" justifyContent="space-between" ml={4}>
        <VStack
          height="80%"
          justifyContent="center"
          pt="6rem"
          spacing={10}
          textAlign="center"
          width="90%"
          zIndex={5}
        >
          <LoadingText size="xl" text="Extracting Data..." />
          <Text textStyle="displayXxs" width="80%">
            Don&apos;t leave, this should only take a minute.
          </Text>

          <Text color="greyScale.700" textStyle="bodyXl500" width="90%">
            Once the data is extracted, you will have a chance to verify the policy data. Or, if you would
            rather enter the data manually, select the SKIP button.
          </Text>

          <OcrInfoBubble />
        </VStack>

        <Flex alignItems="center" height="10%" justifyContent="flex-end" pr="2.5rem" width="100%" />
      </Flex>

      <Flex alignItems="center" flexBasis="30%" flexDirection="column" height="100%" justifyContent="center" p="8rem">
        <Box mb={2}>
          {policyDataStreamIsFetching && <Text>Connecting...</Text>}
          {policyDataStreamIsStreaming && <Text>Extracting Policy Data...</Text>}
          {!policyDataStreamIsFetching && !policyDataStreamIsStreaming && policyDataStream && <Text>Parsing Data...</Text>}
        </Box>

        <Box
          bg="#282a36"
          borderRadius="md"
          h="450px"
          position="relative"
          w="640px"
        >
          <Box
            h="100%"
            overflowY="auto"
            px={4}
            ref={containerRef}
            onScroll={handleScroll}
          >
            {policyDataStreamIsStreaming ? (
              <Box
                as="pre"
                background="black"
                color="white"
                fontFamily="mono"
                fontSize="sm"
                m={0}
                whiteSpace="pre-wrap"
              >
                {displayedStream}
              </Box>
            ) : (
              <SyntaxHighlighter
                customStyle={{
                  margin: 0,
                  background: 'transparent'
                }}
                language="javascript"
                style={dracula}
              >
                {displayedStream}
              </SyntaxHighlighter>
            )}
          </Box>

          {!isAtBottom && (
            <Button
              borderRadius="full"
              bottom="20px"
              boxShadow="lg"
              position="absolute"
              right="20px"
              size="sm"
              onClick={scrollToBottom}
            >
              ↓ New Content
            </Button>
          )}
        </Box>
        <Box my={2}>
          <Text textAlign="center" textStyle="bodySm">PINS AI Assistant can make mistakes.</Text>
          <Text textAlign="center" textStyle="bodySm">Please verify that the policy data is correct before submitting.</Text>
        </Box>

        <HStack justifyContent="space-between" mt={4} spacing={10}>
          <Button
            data-cy={CypressIds.ocrSkipButton}
            dusk={CertSubmissionDuskIds.skipOcrButton}
            size="lg"
            variant="secondary"
            onClick={() => {
              cancel();
              handleSkipOcr(true);
            }}
          >
            SKIP
          </Button>
          {showRetry && <Button size="lg" onClick={() => retryLogic()}>Retry</Button>}
        </HStack>
      </Flex>
    </Flex>
  );
};

OcrProcessingStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default OcrProcessingStep;
